import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getCourseApprovalANDApprovedANDRejected, approveCourse } from '../../../services/adminService';
import { encodeData } from '../../../utils/encodeAndEncode';
import { usePermissions } from '../../../context/PermissionsContext';
import Loader from '../../../components/common/Loader';

const defaultCourseImage = '/images/admin/course/course1.png';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

function DashboardPendingApproval() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvingId, setApprovingId] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getCourseApprovalANDApprovedANDRejected();
      const pending = response?.data?.approve || [];
      setData(pending.slice(0, 5));
    } catch (err) {
      console.error('Failed to fetch pending approvals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApprove = async (e, courseId) => {
    e.stopPropagation();
    if (!permissions.course_approval_workflow_approve) {
      toast.warning("You don't have permission to approve courses");
      return;
    }
    try {
      setApprovingId(courseId);
      await approveCourse({ id: courseId });
      setData(prev => prev.filter(c => c.id !== courseId));
      toast.success('Course approved successfully!');
    } catch {
      toast.error('Failed to approve course. Please try again.');
    } finally {
      setApprovingId(null);
    }
  };

  const handleViewCourse = (courseId) => {
    navigate(`/admin/approvalRequest/view/${encodeData(courseId)}`);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Pending Approval</h2>
          {!loading && (
            <p className="text-xs text-gray-400 mt-0.5">
              {data.length} course{data.length !== 1 ? 's' : ''} awaiting review
            </p>
          )}
        </div>
        <button
          onClick={() => navigate('/admin/approvalRequest')}
          className="text-xs font-medium text-[#7083FF] hover:text-[#5a6be8] flex items-center gap-1 transition-colors"
        >
          View All <Icon icon="lucide:arrow-right" width="13" />
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
          <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
            <Icon icon="lucide:clipboard-check" width="22" className="text-gray-300" />
          </div>
          <p className="text-sm font-medium text-gray-400">No pending approvals</p>
          <p className="text-xs text-gray-300 mt-0.5">All courses are up to date</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '720px' }}>
            {/* Column headers */}
            <thead>
              <tr className="bg-gray-50/80 border-b border-gray-100">
                <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" style={{ width: '38%' }}>
                  Course
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" style={{ width: '16%' }}>
                  Instructor
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" style={{ width: '14%' }}>
                  Submitted
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" style={{ width: '12%' }}>
                  Status
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider" style={{ width: '20%' }}>
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {data.map((item) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50/60 transition-colors cursor-pointer"
                  onClick={() => handleViewCourse(item.id)}
                >
                  {/* Course: thumbnail + name + meta */}
                  <td className="px-5 py-3.5 align-middle">
                    <div className="flex items-center gap-3">
                      <img
                        src={item.banner_image || defaultCourseImage}
                        alt={item.course_name}
                        onError={(e) => { e.target.src = defaultCourseImage; }}
                        className="w-16 h-10 rounded-lg object-cover border border-gray-100 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-800 line-clamp-1 mb-1">
                          {item.course_name}
                        </p>
                        <div className="flex items-center gap-2.5 flex-wrap">
                          {item.course_summary?.total_videos != null && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                              <Icon icon="tabler:video" width="10" />
                              {item.course_summary.total_videos} Videos
                            </span>
                          )}
                          {item.course_summary?.total_documents != null && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                              <Icon icon="tabler:file-text" width="10" />
                              {item.course_summary.total_documents} Docs
                            </span>
                          )}
                          {item.course_summary?.total_duration && (
                            <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                              <Icon icon="tabler:clock" width="10" />
                              {item.course_summary.total_duration?.split('.')[0] || '0:00'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Instructor */}
                  <td className="px-4 py-3.5 align-middle">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                        <span className="text-[10px] font-bold text-violet-600">
                          {(item.trainer_name || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-xs text-gray-700 truncate max-w-[80px]">{item.trainer_name || '—'}</span>
                    </div>
                  </td>

                  {/* Submitted */}
                  <td className="px-4 py-3.5 align-middle">
                    <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(item.request_date)}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3.5 align-middle">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-amber-50 border border-amber-200 text-amber-700 whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                      {item.status || 'Pending'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3.5 align-middle">
                    <div
                      className="flex items-center gap-2"
                      onClick={e => e.stopPropagation()}
                    >
                      {permissions.course_approval_workflow_approve && (
                        <button
                          onClick={(e) => handleApprove(e, item.id)}
                          disabled={approvingId === item.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                          <Icon icon="lucide:check" width="11" />
                          {approvingId === item.id ? 'Approving…' : 'Approve'}
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewCourse(item.id); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100 transition-colors whitespace-nowrap"
                      >
                        <Icon icon="lucide:eye" width="11" />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DashboardPendingApproval;
